/**
 * Who the app thinks you are, and how sure it is.
 *
 * Deliberately thin. Authorization is **not** decided here: the backend enforces
 * area assignment, `can_enter_counts`, `can_post` and session ownership, with no
 * admin bypass (phase3 doc § 1, property 5). This context answers only "is there
 * an identity to send with a request, and where did it come from" — the UI
 * reflects the server's rules, it does not re-implement them.
 *
 * Two identity paths, and **the server chooses which one is real** (§ 6.4, fence
 * 2). `auth_mode: "dev"` plus a dev-mode bundle gives the UPN shim; `entra` gives
 * MSAL. Neither is reachable from the other, and a bundle's own belief is never
 * enough on its own — which is why `authMode` comes from `/auth/config` and not
 * from `import.meta.env`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  setAccessTokenProvider,
  setServerAuthMode,
  setUnauthorizedHandler,
} from "../api/client";
import { useAuthConfig } from "../api/queries";
import type { AuthConfig } from "../api/types";
import { DEV_SHIM_COMPILED_IN, readDevUpn, writeDevUpn } from "./devShim";
import { getEntraAuth, type EntraAuth } from "./msalAuth";

export interface AuthState {
  /** `/auth/config`, or null while it is in flight or failed. */
  config: AuthConfig | null;
  configLoading: boolean;
  configError: Error | null;
  /** The server's mode — the authority on which sign-in path is real. */
  authMode: string | null;
  /** True while the dev shim is both compiled in and sanctioned by the server. */
  devShimActive: boolean;
  /** The pretend identity, when the shim is active. */
  devUpn: string | null;
  /** Whether a request would carry an identity at all. */
  signedIn: boolean;

  // --- The entra path (D7) ---------------------------------------------------
  /** True in `entra` mode once MSAL is constructed and any redirect is finished. */
  entraReady: boolean;
  /** MSAL could not be initialised at all — a configuration fault, not a sign-in one. */
  entraInitError: Error | null;
  /** The signed-in staff UPN, lower-cased as the backend authorises it. */
  upn: string | null;
  /** What to show a person: their display name, falling back to the UPN. */
  displayName: string | null;
  /**
   * Why they are looking at the sign-in screen instead of their count.
   *
   * Set when a silent renewal needs interaction, or when the API answers 401 —
   * i.e. every path where an identity that existed a moment ago no longer works.
   * Cleared by a successful sign-in. **This is the difference between a clear
   * message and a dead screen**, so it carries the server's or MSAL's own words
   * rather than a house sentence.
   */
  sessionMessage: string | null;
  /** Start the redirect flow. A manager action, never automatic. */
  signIn: () => void;
  /** True from the moment a sign-in redirect is asked for until the page leaves. */
  signInPending: boolean;

  signInAsDev: (upn: string) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: config, isLoading, error } = useAuthConfig();
  const [devUpn, setDevUpn] = useState<string | null>(() => readDevUpn());

  const [entra, setEntra] = useState<EntraAuth | null>(null);
  const [entraInitError, setEntraInitError] = useState<Error | null>(null);
  const [account, setAccount] = useState<{ upn: string; displayName: string | null } | null>(
    null,
  );
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);
  const [signInPending, setSignInPending] = useState(false);

  const authMode = config?.auth_mode ?? null;
  const entraMode = authMode === "entra";

  // The client reads this when deciding whether to send `X-Dev-User`, so it has
  // to be set the moment the config lands and cleared if it ever un-lands.
  useEffect(() => {
    setServerAuthMode(authMode);
  }, [authMode]);

  /**
   * Build MSAL once the server says `entra` and names a registration.
   *
   * Gated on `entraMode` rather than on the bundle: a dev-mode service — the
   * tunnel path — never constructs MSAL, never touches `localStorage` for auth,
   * and behaves exactly as it did before D7.
   */
  useEffect(() => {
    if (!entraMode || !config?.client_id) return;
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    (async () => {
      try {
        const auth = await getEntraAuth(config);
        if (cancelled) return;
        setEntra(auth);
        setEntraInitError(null);
        const upn = auth.upn();
        setAccount(upn ? { upn, displayName: auth.displayName() } : null);
        // From here on MSAL's own events are the authority on which account is
        // signed in — a redirect completing after this point, a token being
        // acquired, a sign-out in another tab. See `onAccountChange` for why the
        // subscription is here rather than in `MsalProvider`.
        unsubscribe = auth.onAccountChange((account) => {
          if (cancelled) return;
          setAccount(
            account?.username
              ? {
                  upn: account.username.toLowerCase(),
                  displayName: account.name ?? account.username,
                }
              : null,
          );
        });
      } catch (exc) {
        if (cancelled) return;
        setEntra(null);
        setEntraInitError(exc instanceof Error ? exc : new Error(String(exc)));
      }
    })();
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [entraMode, config]);

  /**
   * Hand the fetch layer a way to get a token, and a place to report a 401.
   *
   * Both are registered **only in entra mode**, so dev mode's request path is
   * byte-for-byte the one D1 shipped. The token provider is a function of the
   * live MSAL instance rather than a captured string: see `msalAuth.token`.
   */
  const entraRef = useRef<EntraAuth | null>(null);
  entraRef.current = entra;

  /**
   * Whether an identity existed a moment ago.
   *
   * A 401 means two very different things depending on this. If somebody was
   * signed in, their session ended and they need to be told. If nobody was — the
   * app has just booted, or a screen fired a request before MSAL finished
   * initialising — then a 401 is simply "not signed in yet", and announcing *"your
   * session has ended"* would be a false alarm on the very first screen of the
   * day. A ref rather than state because the handler is registered once and must
   * read the value at call time, not at registration time.
   */
  const hadAccountRef = useRef(false);
  hadAccountRef.current = Boolean(account);

  useEffect(() => {
    if (!entraMode) return;
    setAccessTokenProvider(async () => {
      const auth = entraRef.current;
      if (!auth) return null;
      const result = await auth.token();
      if (!result.token && hadAccountRef.current) {
        // A renewal that needs a human is the *expected* end of a long session,
        // so it explains itself here rather than surfacing as a bare 401 later.
        setAccount(null);
        setSessionMessage(
          result.interactionRequired
            ? `Your sign-in has expired. ${result.reason ?? ""}`.trim()
            : `Could not renew your sign-in: ${result.reason ?? "unknown error"}`,
        );
      }
      return result.token;
    });
    setUnauthorizedHandler((detail) => {
      if (!hadAccountRef.current) return;
      setAccount(null);
      setSessionMessage(detail);
    });
    return () => {
      setAccessTokenProvider(null);
      setUnauthorizedHandler(null);
    };
  }, [entraMode]);

  const signInAsDev = useCallback((upn: string) => {
    const trimmed = upn.trim();
    if (!trimmed) return;
    writeDevUpn(trimmed);
    setDevUpn(trimmed);
  }, []);

  const signIn = useCallback(() => {
    const auth = entraRef.current;
    if (!auth) return;
    setSignInPending(true);
    setSessionMessage(null);
    auth.signIn().catch((exc) => {
      setSignInPending(false);
      setSessionMessage(
        `Sign-in could not start: ${exc instanceof Error ? exc.message : String(exc)}`,
      );
    });
  }, []);

  const signOut = useCallback(() => {
    // Dev first, and unconditionally: clearing a pretend identity costs nothing
    // and a dev bundle folds the entra half away.
    writeDevUpn(null);
    setDevUpn(null);
    const auth = entraRef.current;
    if (!auth) return;
    setAccount(null);
    setSessionMessage(null);
    auth.signOut().catch((exc) => {
      setSessionMessage(
        `Sign-out did not complete: ${exc instanceof Error ? exc.message : String(exc)}`,
      );
    });
  }, []);

  const devShimActive = DEV_SHIM_COMPILED_IN && authMode === "dev";
  const entraSignedIn = entraMode && Boolean(account);

  const value = useMemo<AuthState>(
    () => ({
      config: config ?? null,
      configLoading: isLoading,
      configError: (error as Error | null) ?? null,
      authMode,
      devShimActive,
      devUpn: devShimActive ? devUpn : null,
      signedIn: devShimActive ? Boolean(devUpn) : entraSignedIn,
      entraReady: entraMode && Boolean(entra),
      entraInitError,
      upn: entraSignedIn ? (account?.upn ?? null) : devShimActive ? devUpn : null,
      displayName: entraSignedIn
        ? (account?.displayName ?? account?.upn ?? null)
        : devShimActive
          ? devUpn
          : null,
      sessionMessage,
      signIn,
      signInPending,
      signInAsDev,
      signOut,
    }),
    [
      config,
      isLoading,
      error,
      authMode,
      devShimActive,
      devUpn,
      entraMode,
      entra,
      entraInitError,
      entraSignedIn,
      account,
      sessionMessage,
      signIn,
      signInPending,
      signInAsDev,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
